import { workspace } from '../../shared/api';
import { PRESCRIPTION_STATUS } from '../../interfaces/prescription';
import { ManualMedicine, Medicine } from '../../interfaces/medicine';
import { Exam } from '../../interfaces/exam';
import { MedicalCertificate } from '../../interfaces/medicalCertificate';
import { MedicalReferral } from '../../interfaces/MedicalReferral';
import { MedicalReport } from '../../interfaces/medicalReport';
import { CANCELLABLE_DOCS } from '../../components/CancelByDocument';
import { DialogContent } from '../../shared/prescriber';
import { Formula } from '../../interfaces/formula';

/**
 * Endpoints do protocols controller (nx-workspace):
 *
 * - `GET    /protocols`     — lista com filtros e paginação
 * - `GET    /protocols/:id` — detalhes (id é CUID2/UUID, string)
 * - `POST   /protocols`     — cria (201)
 * - `PATCH  /protocols/:id` — update parcial com upsert por item (204)
 * - `DELETE /protocols/:id` — soft delete (204)
 */
const PROTOCOLS_BASE = '/protocols';

export const MODELS_PAGE_SIZE = 20;

/**
 * Tamanho de página usado só na checagem de nome duplicado. O backend filtra por
 * `LIKE %name%`, então um termo curto traz muitos parciais. 50 é o teto aceito pelo BFF
 * (`@Max(50)` no ListProtocolsQueryDto).
 */
const DUPLICATE_NAME_LOOKUP_LIMIT = 50;

/**
 * Teto de páginas percorridas na checagem de nome duplicado.
 *
 * Como o filtro é parcial, o homônimo exato pode estar fora da primeira página; varremos as
 * seguintes até achá-lo ou esgotar o resultado. O teto existe só para não transformar um
 * termo muito genérico numa varredura da biblioteca inteira.
 */
const DUPLICATE_NAME_LOOKUP_MAX_PAGES = 10;

const normalizeModelName = (value: string) => value.trim().toLowerCase();

export const ModelService = {
  /**
   * Lista os modelos (protocols) do médico/parceiro.
   *
   * Não filtra patrocinados aqui; use {@link ModelService.fetchSponsoredModels} para isso.
   * `includeSponsoredModels` mistura os dois grupos na mesma resposta.
   *
   * @param doctorId prescritor dono dos modelos
   * @param prescriptionId prescrição em contexto, exigida pelo endpoint
   * @param partnerId parceiro do prescritor, exigido pelo backend
   * @param includeSponsoredModels inclui os patrocinados no resultado
   * @param page página desejada, 1-based
   * @param limit tamanho da página
   * @param age faixa etária do paciente, usada pelo backend para priorizar modelos
   * @param name filtro por nome; o backend compara por `LIKE %name%`
   * @param speciality especialidades (idRqe) do médico, para priorizar/filtrar modelos
   * @returns a página pedida, ou uma página vazia quando o backend não devolve corpo
   */
  async fetchModels(
    doctorId: number,
    prescriptionId: number,
    partnerId: number,
    includeSponsoredModels: boolean = false,
    page: number = 1,
    limit: number = MODELS_PAGE_SIZE,
    age?: string,
    name?: string,
    speciality?: string[],
  ): Promise<ProtocolListResponse> {
    const params = {
      doctorId,
      partnerId,
      prescriptionId,
      page,
      limit,
      ...(includeSponsoredModels && { includeSponsoredModels: true }),
      ...(age && { age }),
      ...(name && { name }),
      ...(speciality?.length ? { speciality } : {}),
    };

    const { data } = await workspace.get<ProtocolListResponse>(PROTOCOLS_BASE, { params });
    return data ?? { data: [], total: 0, page, limit };
  },

  /**
   * Retorna apenas os modelos com `sponsored = true` no payload.
   *
   * Reutiliza o mesmo endpoint com `includeSponsoredModels=true` e filtra client-side. O
   * gating por feature flag (`Features.enableSponsoredModel`) acontece antes desta chamada,
   * no `useModelStore.fetchSponsoredModels` — com a flag desligada este método nem roda.
   *
   * @param doctorId prescritor dono dos modelos
   * @param prescriptionId prescrição em contexto, exigida pelo endpoint
   * @param partnerId parceiro do prescritor, exigido pelo backend
   * @param age faixa etária do paciente, usada pelo backend para priorizar modelos
   * @returns só os modelos patrocinados, com o total recalculado
   */
  async fetchSponsoredModels(
    doctorId: number,
    prescriptionId: number,
    partnerId: number,
    age?: string,
  ): Promise<ProtocolListResponse> {
    const params = {
      doctorId,
      partnerId,
      prescriptionId,
      includeSponsoredModels: true,
      ...(age && { age }),
    };

    const { data } = await workspace.get<ProtocolListResponse>(PROTOCOLS_BASE, { params });
    const filtered = (data?.data ?? []).filter((p) => p.sponsored);
    return {
      data: filtered,
      total: filtered.length,
      page: data?.page ?? 1,
      limit: data?.limit ?? filtered.length,
    };
  },

  /**
   * Remove um modelo (soft delete, 204).
   *
   * Em auth Basic o token não carrega `doctorId`/`partnerId` (é credencial de parceiro), e o
   * controller do nx-workspace os resolve pela query como fallback. Enviamos sempre — em auth
   * Bearer o backend resolve pelo token e ignora a query.
   *
   * `prescriptionId` resolve o subparceiro em contexto; sem ele o modelo institucional de
   * subparceiro fica fora do escopo da busca e a exclusão responde 404.
   *
   * @param modelId id do modelo (CUID2/UUID)
   * @param doctorId prescritor dono do modelo
   * @param partnerId parceiro do prescritor
   * @param prescriptionId prescrição em contexto, usada para resolver o subparceiro
   */
  async deleteModel(modelId: string, doctorId: number, partnerId: number, prescriptionId: number): Promise<void> {
    await workspace.delete(`${PROTOCOLS_BASE}/${modelId}`, {
      params: { doctorId, partnerId, prescriptionId },
    });
  },

  /**
   * Procura um modelo com exatamente o mesmo nome dentro da origem informada, para bloquear
   * homônimos na criação e na renomeação. O backend filtra por `LIKE %name%`, então a
   * comparação exata é feita aqui.
   *
   * A origem entra na comparação porque um modelo pessoal e um institucional podem coexistir
   * com o mesmo nome — são listas distintas para o prescritor.
   *
   * @param doctorId id do prescritor dono do modelo
   * @param prescriptionId prescrição em contexto, exigida pelo endpoint de listagem
   * @param partnerId parceiro do prescritor, exigido pelo endpoint de listagem
   * @param name nome candidato, comparado sem distinção de caixa e espaços nas pontas
   * @param origin origem em que o nome precisa ser único
   * @param excludeId modelo a ignorar na comparação — o próprio, no caso de renomeação
   * @returns o modelo homônimo da mesma origem, ou `null` quando não há
   */
  async findModelByName(
    doctorId: number,
    prescriptionId: number,
    partnerId: number,
    name: string,
    origin: ProtocolOrigin,
    excludeId?: string,
  ): Promise<ProtocolSummary | null> {
    const trimmed = name.trim();
    if (!trimmed) return null;

    const normalizedName = normalizeModelName(trimmed);
    const isDuplicate = (model: ProtocolSummary) =>
      model.origin === origin && model.id !== excludeId && normalizeModelName(model.name) === normalizedName;

    let page = 1;
    let scanned = 0;

    while (page <= DUPLICATE_NAME_LOOKUP_MAX_PAGES) {
      const response = await ModelService.fetchModels(
        doctorId,
        prescriptionId,
        partnerId,
        false,
        page,
        DUPLICATE_NAME_LOOKUP_LIMIT,
        undefined,
        trimmed,
      );

      const data = response.data ?? [];
      const match = data.find(isDuplicate);
      if (match) return match;

      scanned += data.length;
      if (data.length < DUPLICATE_NAME_LOOKUP_LIMIT || scanned >= (response.total ?? scanned)) break;
      page += 1;
    }

    return null;
  },

  /**
   * Carrega os detalhes de um modelo.
   *
   * `prescriptionId` é o que permite ao backend resolver o subparceiro em contexto. Sem ele o
   * escopo de visibilidade sai com `sub_partner_id IS NULL` e um modelo institucional de
   * subparceiro — que aparece na listagem — responde 404 aqui.
   *
   * `doctorId` e `partnerId` acompanham porque em autenticação Basic a sessão não tem médico:
   * o BFF prefere a sessão e só cai para estes valores quando ela não os traz, então em Bearer
   * eles são ignorados.
   *
   * @param modelId id do modelo (CUID2/UUID)
   * @param doctorId prescritor em contexto
   * @param partnerId parceiro do prescritor
   * @param prescriptionId prescrição em contexto, usada para resolver o subparceiro
   * @returns os detalhes do modelo
   */
  async fetchModelDetails(
    modelId: string,
    doctorId: number,
    partnerId: number,
    prescriptionId: number,
  ): Promise<ProtocolDetail> {
    const { data } = await workspace.get<ProtocolDetail>(`${PROTOCOLS_BASE}/${modelId}`, {
      params: { doctorId, partnerId, prescriptionId },
    });
    return data;
  },

  async createModel(payload: CreateProtocolPayload): Promise<{ id: string; name: string }> {
    const { data } = await workspace.post<{ id: string }>(PROTOCOLS_BASE, payload);
    return { id: data.id, name: payload.name };
  },

  /**
   * Atualiza um modelo existente.
   *
   * Contrato: `PATCH /protocols/:id` — upsert por item nas coleções internas, 204 No Content.
   * Campos editáveis: `name`, `cid`, `ageRange`, `tagIds`, `publicationStatus` e as listas de
   * itens (`medications`, `exams`, `certificates`, `referrals`, `reports`), com `_destroy`
   * para remoção.
   *
   * @param modelId id do modelo (CUID2/UUID)
   * @param payload alterações a aplicar
   */
  async updateModel(modelId: string, payload: UpdateProtocolPayload): Promise<void> {
    await workspace.patch(`${PROTOCOLS_BASE}/${modelId}`, payload);
  },

  async updatePrescriberDialogPharmaModels(pharmaModel: DialogContent, prescriberId: number): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      dialogs: { pharmaModel },
    });
  },
};

export const HistoryService = {
  async fetchPrescriptionHistory(payload: {
    patientId: number;
    prescriberId: number;
    establishmentId: number;
    page: number;
    prescriptionId: number;
  }): Promise<PrescriptionHistory> {
    const params = {
      idPaciente: payload.patientId,
      idMedico: payload.prescriberId,
      idEstabelecimento: payload.establishmentId,
      idPrescricao: payload.prescriptionId,
      pageSize: '10',
      page: payload.page,
    };

    const { data } = await workspace.get(`/pacientes/${params.idPaciente}/prescricoes`, { params });

    const mappedPrescriptionHistory = data.Prescricoes.map((item: HistoryPrescription) => {
      return {
        ...item,
      };
    });

    return {
      Medico: data.Medico,
      Prescricoes: mappedPrescriptionHistory,
      PossuiProximaPagina: data.PossuiProximaPagina,
    };
  },
  async cancelPrescriptionHistory(
    reason: string,
    prescriptionId: number,
    prescriptionToBeCanceled: number
  ): Promise<void> {
    await workspace.delete(`/prescricao/${prescriptionId}/historico`, {
      data: { motivoCancelamento: reason || '', idPrescricaoACancelar: prescriptionToBeCanceled },
    });
  },

  async cancelPrescriptionHistoryByDocument(
    prescriptionToBeCanceled: number,
    documentsToBeCancelled: CANCELLABLE_DOCS[]
  ): Promise<void> {
    await workspace.delete(`/prescricao/${prescriptionToBeCanceled}/documentos`, {
      data: { tiposDocumento: documentsToBeCancelled },
    });
  },
};

/**
 * @deprecated Use `ProtocolListResponse` (shape EN do novo contrato `/protocols`).
 */
export type ModelListResponse = ProtocolListResponse;

export interface HistoryPrescriptionStatus {
  idPrescricaoStatus: PRESCRIPTION_STATUS;
  Descricao: string;
}

export interface HistoryMedicine {
  idPrescricaoMedicamento: number;
  idMedicamento: number;
  Comentario: string;
  Posologia: string;
  UsoContinuo: boolean;
  Quantidade: number;
  QuantidadeEmbalagens: string;
  Medicamento: HistoryMedicine2;
  medicamentoLme: HistoryMedicineLme | null;
  Ordem: number | null;
}

export interface HistoryMedicineLme {
  cid: string;
  unidades: {
    unidade: number;
    mes: number;
  }[];
}

export interface HistoryMedicine2 {
  idMedicamento: number;
  idPortaria344: number;
  idTipoMedicamento: number;
  ControleEspecial: boolean;
  Nome: string;
  Concentracao: string;
  Unidade: string;
}

export interface HistoryManualMedicine {
  Nome: string;
  Comentario: string;
  idMedicamentoManual: number;
  Label: string;
  idPrescricaoMedicamentoManual: number;
  Quantidade: number;
  Posologia: string;
  Embalagem: string;
  UsoContinuo: boolean;
  UsoInterno: boolean;
  Ordem: number | null;

  // esses campos são fixos, como tem a ver com dispensação não vamos preencher
  DetalhesDispensa: [];
  QuantidadeDispensada: 0;
  Dispensado: false;
}

export interface HistoryPrescription {
  idPrescricao: number;
  CodigoValidacao: string;
  UrlQrCode: string;
  Status: HistoryPrescriptionStatus;
  Medicamentos: HistoryMedicine[];
  MedicamentosManuais: HistoryManualMedicine[];
  Formulas: Formula[];
  Exames: Exam[];
  Atestados: MedicalCertificate[];
  DataCriacao: string;
  Referencia: string;
  Encaminhamentos: MedicalReferral[];
  Relatorios: MedicalReport[];
  Medico: Doctor;
  PodeCancelar: boolean;
  habilitarBotaoMais: boolean;
  cancelados: CANCELLABLE_DOCS[];
}

export interface Doctor {
  idMedico: number;
}

export interface PrescriptionHistory {
  Medico: Doctor;
  Prescricoes: HistoryPrescription[];
  PossuiProximaPagina: boolean;
}

/**
 * @deprecated Use `ProtocolSummary` (shape EN do novo contrato).
 */
export type Model = ProtocolSummary;

/**
 * @deprecated Use `ProtocolDetail` (shape EN do novo contrato).
 */
export type ModelDetails = ProtocolDetail;

export type ProtocolDocumentType =
  | 'medication'
  | 'controlled_medication'
  | 'exam'
  | 'certificate'
  | 'referral'
  | 'report';

export type ProtocolOrigin = 'personal' | 'institutional' | 'sponsored';

export type ProtocolPublicationStatus = 'draft' | 'published' | 'archived';

export interface ProtocolTag {
  id: string;
  name: string;
  scope: ProtocolOrigin;
}

/**
 * Posologia estruturada preservada no modelo.
 *
 * Substitui o antigo `structuredDosage: Record<string, unknown>` por um shape tipado,
 * alinhado ao que a UI de prescrição usa para "posologia + quantidade + embalagem".
 */
export interface StructuredPosology {
  totalTreatment: string;
  quantity: string;
  unit: string;
  unitPlural: string;
  suggestedPackaging: number;
  suggestedPackagingQuantity: number;
}

/**
 * Unidade prescrita em um dos seis meses do LME.
 *
 * O modelo guarda a distribuição, e não só o total em `lmeTotalTreatment6m`: a inclusão do
 * medicamento na prescrição exige as unidades mês a mês, com o 1º mês maior que zero.
 */
export interface ProtocolLmeUnit {
  month: number;
  unit: number;
}

export interface ProtocolMedication {
  id: string;
  /** Flag autoritativa do backend — fonte da verdade para catálogo × manual (ver `isCatalogMedication`). */
  isManual: boolean;
  /** Código EAN/GTIN-13 (primeiro item de `Medicine.ListaEANS`); `null` se manual ou sem EAN cadastrado. */
  ean: string | null;
  /** Id do medicamento no catálogo Mevo (`Medicine.IdMedicamento`); `null` se manual. */
  medicationId: string | null;
  brandName: string | null;
  activeIngredient: string | null;
  posology: string | null;
  continuousUse: boolean;
  structuredPosology: StructuredPosology | null;
  isLme: boolean;
  lmeCid: string | null;
  lmeCnes: string | null;
  lmeDoctorCns: string | null;
  lmeAnamnesis: string | null;
  /** Do atendimento, não do modelo: CNES, CNS, anamnese e consentimento não são salvos. */
  lmeInformedConsent: boolean;
  /** Só leitura: derivado pelo backend a partir de `lmeUnits`, não é enviado na criação. */
  lmeTotalTreatment6m: number | null;
  lmeUnits: ProtocolLmeUnit[] | null;
  quantity: number | null;
  packaging: string | null;
  /** Valor de `PT_PRESCRIPTION_TYPE` (ver interfaces/medicine.ts); relevante sobretudo para manual. */
  prescriptionType: string | null;
  /** `ManualMedicine.Manipulado`. */
  isCompounded: boolean;
  allowSubstitution: boolean;
  /** Laboratório escolhido pelo prescritor para o genérico, quando houve escolha. */
  preferredLaboratoryId: number | null;
  useInternal: boolean;
  isStructuredPosology: boolean;
  isAntimicrobial: boolean;
  antimicrobialTypeId: number | null;
  isRelatedProduct: boolean;
  /** `Medicine.medicineIcd?.icd10`; só existe para catálogo (sem equivalente em ManualMedicine). */
  icdCode: string | null;
  sortOrder: number;
}

export interface ProtocolExamItem {
  id: string;
  tussCode: string | null;
  description: string | null;
  specificCid: string | null;
  observation: string | null;
  laterality: string | null;
  sessionCount: number | null;
  apac: boolean;
  bpaI: boolean;
  sortOrder: number;
}

export interface ProtocolExam {
  id: string;
  /** Antes era `prescriptionCid: string | null`; virou array para suportar múltiplos CIDs. */
  prescriptionCids: string[] | null;
  generalObservation: string | null;
  sortOrder: number;
  items: ProtocolExamItem[];
}

export interface ProtocolCertificate {
  id: string;
  reason: string | null;
  observation: string | null;
  type: string | null;
  period: string | null;
  restPeriodType: string | null;
  diagnosisCid: string | null;
  secondaryCids: string[] | null;
  consentText: string | null;
  hasConsent: boolean;
  isFreeText: boolean;
  sortOrder: number;
}

export interface ProtocolReferral {
  id: string;
  specialty: string | null;
  /** Resolve o gap de `Especialidade.IdEspecialidade` legado. */
  specialtyId: number | null;
  diagnosisCid: string | null;
  reason: string | null;
  clinicalHistory: string | null;
  conduct: string | null;
  /** Corresponde a `MedicalReferral.tipoParecer`. */
  reportType: string | null;
  sortOrder: number;
}

export interface ProtocolReport {
  id: string;
  documentType: string | null;
  description: string | null;
  sortOrder: number;
}

/**
 * Fórmula manipulada dentro de um modelo.
 *
 * Referencia uma fórmula pré-cadastrada da biblioteca do médico por `formulaId`; os dados
 * completos são resolvidos pelo backend na hora de aplicar.
 */
export interface ProtocolCompoundedFormula {
  id: string;
  formulaId: number;
  sortOrder: number;
}

export interface ProtocolSummary {
  id: string;
  name: string;
  documentType: ProtocolDocumentType;
  origin: ProtocolOrigin;
  publicationStatus: ProtocolPublicationStatus;
  partnerId: number;
  subPartnerId: number | null;
  cid: string | null;
  ageRange: string | null;
  tags: ProtocolTag[];
  sponsored: boolean;
  /**
   * Quantidade total de itens do modelo (medications + exams.items + certificates +
   * referrals + reports + compoundedFormulas). Renderizada no badge "N itens" dos cards.
   * Calculada server-side no protocols-service (`toSummary`) e exposta pelo BFF.
   */
  itemsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProtocolDetail {
  id: string;
  name: string;
  /** Observação da prescrição guardada no modelo. `null` em modelos migrados do legado. */
  observation: string | null;
  doctorId: number;
  documentType: ProtocolDocumentType;
  partnerId: number;
  subPartnerId: number | null;
  cid: string | null;
  ageRange: string | null;
  publicationStatus: ProtocolPublicationStatus;
  origin: ProtocolOrigin;
  createdAt: string;
  updatedAt: string;
  tags: ProtocolTag[];
  medications: ProtocolMedication[];
  exams: ProtocolExam[];
  certificates: ProtocolCertificate[];
  referrals: ProtocolReferral[];
  reports: ProtocolReport[];
  compoundedFormulas: ProtocolCompoundedFormula[];
}

export interface ProtocolListResponse {
  data: ProtocolSummary[];
  total: number;
  page: number;
  limit: number;
  /**
   * Se o prescritor administra os modelos institucionais do escopo em contexto.
   *
   * Vem na listagem, e não no perfil do médico, porque a permissão depende do subparceiro —
   * que só é conhecido quando há prescrição em contexto. Resolvida no BFF, que consulta
   * `medicoadmin` na base legada e é também quem barra a escrita de modelo institucional por
   * quem não é administrador.
   */
  administradorModeloInstitucional?: boolean;
}

interface UpsertItem {
  id?: string;
  _destroy?: boolean;
}

/** Inputs de criação (`POST /protocols`) — todos os campos do shape, menos `id`. */
export type CreateProtocolMedicationInput = Partial<Omit<ProtocolMedication, 'id'>>;
export type CreateProtocolExamItemInput = Partial<Omit<ProtocolExamItem, 'id'>>;
export interface CreateProtocolExamInput {
  prescriptionCids?: string[] | null;
  generalObservation?: string | null;
  sortOrder?: number;
  items: CreateProtocolExamItemInput[];
}
export type CreateProtocolCertificateInput = Partial<Omit<ProtocolCertificate, 'id'>>;
export type CreateProtocolReferralInput = Partial<Omit<ProtocolReferral, 'id'>>;
export type CreateProtocolReportInput = Partial<Omit<ProtocolReport, 'id'>>;
export type CreateProtocolCompoundedFormulaInput = Partial<Omit<ProtocolCompoundedFormula, 'id'>>;

/** Inputs de update (`PATCH /protocols/:id`) — mesmos campos, com suporte a upsert (`id`, `_destroy`). */
export type UpdateProtocolMedicationInput = UpsertItem & CreateProtocolMedicationInput;
export type UpdateProtocolExamItemInput = UpsertItem & CreateProtocolExamItemInput;
export type UpdateProtocolExamInput = UpsertItem & {
  prescriptionCids?: string[] | null;
  generalObservation?: string | null;
  sortOrder?: number;
  items?: UpdateProtocolExamItemInput[];
};
export type UpdateProtocolCertificateInput = UpsertItem & CreateProtocolCertificateInput;
export type UpdateProtocolReferralInput = UpsertItem & CreateProtocolReferralInput;
export type UpdateProtocolReportInput = UpsertItem & CreateProtocolReportInput;
export type UpdateProtocolCompoundedFormulaInput = UpsertItem & CreateProtocolCompoundedFormulaInput;

export interface CreateProtocolPayload {
  /**
   * `doctorId` e `partnerId` são opcionais no backend — ele extrai do JWT primeiro e cai pro
   * body se não estiverem no token. Mantemos obrigatórios aqui como cinto-e-suspensório.
   */
  doctorId: number;
  partnerId: number;
  /**
   * Prescrição em contexto, usada pelo BFF para resolver o subparceiro do prescritor.
   *
   * Nenhuma strategy de autenticação grava subparceiro na sessão, então sem este campo o
   * `resolveActor` do nx devolve `subPartnerId: null`: um administrador de subparceiro é
   * negado com 403, e o modelo criado nasce no nível do parceiro pai — visível a todos os
   * subparceiros irmãos.
   */
  prescriptionId: number;
  /**
   * Origem do modelo. Omitir equivale a `personal` — o BFF assume esse valor quando não vem
   * nada. `institutional` só é aceito para prescritor administrador do escopo em contexto;
   * caso contrário o BFF responde 403.
   */
  origin?: ProtocolOrigin;
  name: string;
  /** Observação da prescrição a guardar no modelo. Omitir ou `null` grava sem observação. */
  observation?: string | null;
  documentType: ProtocolDocumentType;
  publicationStatus: ProtocolPublicationStatus;
  cid?: string | null;
  ageRange?: string | null;
  tagIds?: string[];
  medications?: CreateProtocolMedicationInput[];
  exams?: CreateProtocolExamInput[];
  certificates?: CreateProtocolCertificateInput[];
  referrals?: CreateProtocolReferralInput[];
  reports?: CreateProtocolReportInput[];
  compoundedFormulas?: CreateProtocolCompoundedFormulaInput[];
}

export interface UpdateProtocolPayload {
  doctorId: number;
  /**
   * Nova origem do modelo, quando o administrador troca entre pessoal e institucional.
   *
   * Aceita os dois sentidos. `sponsored` nunca é destino nem editável. Só enviado quando a
   * origem muda de fato — o backend valida que quem pede é administrador do parceiro.
   */
  origin?: ProtocolOrigin;
  /** Em auth Basic o token não carrega `partnerId`; o controller o resolve pelo body. */
  partnerId: number;
  /** Nova observação do modelo. Omitir mantém a atual; `null` limpa. */
  observation?: string | null;
  /**
   * Prescrição em contexto, usada pelo BFF para resolver o subparceiro do prescritor.
   *
   * Sem ela o `assertCanManage` busca o modelo num escopo sem subparceiro, e o institucional
   * de subparceiro fica invisível — a edição responde 404.
   */
  prescriptionId: number;
  name?: string;
  publicationStatus?: ProtocolPublicationStatus;
  cid?: string | null;
  ageRange?: string | null;
  tagIds?: string[];
  medications?: UpdateProtocolMedicationInput[];
  exams?: UpdateProtocolExamInput[];
  certificates?: UpdateProtocolCertificateInput[];
  referrals?: UpdateProtocolReferralInput[];
  reports?: UpdateProtocolReportInput[];
  compoundedFormulas?: UpdateProtocolCompoundedFormulaInput[];
}

export interface HistoryDocuments {
  idPrescricao: number;
  url: string;
  assinado: boolean;
  assinatura: string;
  tipoDocumento: string;
}
